import React, { useEffect, useRef, useState } from 'react'
import type { PopperPlacementType } from '@mui/material/Popper'
import useMediaQuery from '@mui/material/useMediaQuery'
import CustomizableTooltip from '@/components/ui/CustomizableTooltip'

export type TooltipProps = {
  /** The title of the tooltip. */
  title: string
  /** The placement of the tooltip. */
  placement?: PopperPlacementType
  /** The content of the tooltip. */
  children: React.ReactElement
  /** The x offset of the tooltip. */
  offsetX?: number
  /** The y offset of the tooltip. */
  offsetY?: number
  /**
   * When true, the tooltip bubble stays visible regardless of hover/click. Skips the usual hover
   * and touch listeners. Useful for styling/element inspection in Storybook.
   */
  forceOpen?: boolean
  /**
   * When true, the bubble is non-interactive (`pointer-events: none`), so it never captures the
   * cursor. Use when the bubble may overlap its trigger and you don't want it to steal the
   * trigger's hover state.
   */
  disableInteractive?: boolean
  /** When true, the bubble opens on hover only. */
  disableFocusListener?: boolean
  /**
   * Milliseconds to wait before opening on hover, including when moving from one tooltip to the next.
   * Touch devices open on tap with no delay.
   */
  hoverDelayMs?: number
}

/** True when the primary input has no hover (e.g. touch devices). */
const useClickInsteadOfHover = (): boolean => useMediaQuery('(hover: none)')

/**
 * Tooltip that shows on hover (desktop) or on click/tap (touch devices).
 * Uses the (hover: none) media query to detect touch-primary devices.
 * On touch, wraps the child in a focusable span so the tooltip can be toggled by tap.
 */
export const Tooltip: React.FC<TooltipProps> = ({
  children,
  offsetX = 0,
  offsetY = 0,
  placement = 'top',
  title,
  forceOpen = false,
  disableInteractive = false,
  disableFocusListener = false,
  hoverDelayMs,
}) => {
  const [open, setOpen] = useState(false)
  const useClick = useClickInsteadOfHover()
  const triggerRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (!useClick || !open) return

    // function to determine if the tooltip should be closed
    const handlePointerDown = (e: PointerEvent) => {
      const target = e.target as Node
      const trigger = triggerRef.current
      // If the target is the trigger, don't close the tooltip
      if (trigger?.contains(target)) return
      // If the target is the tooltip, don't close the tooltip
      const tooltipId = trigger?.getAttribute('aria-describedby')
      // If the target is the tooltip, don't close the tooltip
      if (tooltipId && document.getElementById(tooltipId)?.contains(target)) return
      // If the target is not the trigger or tooltip, close the tooltip
      setOpen(false)
    }
    // add the event listener
    document.addEventListener('pointerdown', handlePointerDown)

    // cleanup function to remove the event listener
    return () => document.removeEventListener('pointerdown', handlePointerDown)
  }, [useClick, open])

  const slotProps = {
    popper: {
      modifiers: [
        { name: 'offset', options: { offset: [offsetX, offsetY] } },
        // A `-start` or `-end` tooltip keeps its alignment near the viewport edge and slides to stay in view.
        { name: 'flip', options: { flipVariations: false } },
        // The 16px viewport gutter. The bubble's max width leaves the same gutter on both sides, so a long tooltip wraps inside it.
        { name: 'preventOverflow', options: { padding: 16 } },
      ],
    },
    tooltip: {
      className: 'flex items-center !max-w-[calc(100vw-32px)] !bg-surface-inverse !text-text-on-inverse-primary text-2xs font-medium',
      // Forwarded onto MUI's rendered tooltip element so the visible bubble inspects as `Tooltip`.
      'data-ds-component': 'Tooltip',
      'data-ds-placement': placement,
    },
  }

  if (forceOpen) {
    return (
      <CustomizableTooltip
        open={true}
        disableHoverListener
        disableTouchListener
        disableFocusListener
        disableInteractive={disableInteractive}
        placement={placement}
        slotProps={slotProps}
        title={title}
      >
        {children}
      </CustomizableTooltip>
    )
  }

  if (useClick) {
    // Keep the wrapper span in the DOM regardless of title so ref-bearing children never remount
    // (remounting re-fires useIsTruncated's ResizeObserver callback and causes an update loop).
    // When title is empty there is nothing to toggle, so strip the interactive ARIA and event
    // handlers to avoid swallowing clicks on behalf of an ancestor (e.g. DraftCard's outer button).
    const hasTitle = Boolean(title)
    return (
      <CustomizableTooltip
        disableFocusListener
        disableHoverListener
        disableTouchListener
        disableInteractive={disableInteractive}
        onClose={hasTitle ? () => setOpen(false) : undefined}
        open={hasTitle ? open : false}
        placement={placement}
        slotProps={slotProps}
        title={title}
      >
        <span
          ref={triggerRef}
          data-ds-component="Tooltip"
          data-ds-placement={placement}
          // min-w-0 lets the wrapper shrink in a flex row, so text inside it can still truncate beside its siblings
          className="block w-full min-w-0"
          role={hasTitle ? 'button' : undefined}
          tabIndex={hasTitle ? 0 : undefined}
          onPointerDown={hasTitle ? (e) => e.stopPropagation() : undefined}
          onClick={
            hasTitle
              ? (e) => {
                  e.stopPropagation()
                  setOpen((prev) => !prev)
                }
              : undefined
          }
          onKeyDown={
            hasTitle
              ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    e.stopPropagation()
                    setOpen((prev) => !prev)
                  }
                }
              : undefined
          }
        >
          {children}
        </span>
      </CustomizableTooltip>
    )
  }

  return (
    <CustomizableTooltip
      placement={placement}
      slotProps={slotProps}
      title={title}
      disableInteractive={disableInteractive}
      disableFocusListener={disableFocusListener}
      enterDelay={hoverDelayMs}
      // Without this, MUI skips the delay for a moment after another tooltip closes.
      enterNextDelay={hoverDelayMs}
    >
      {children}
    </CustomizableTooltip>
  )
}

export { useClickInsteadOfHover }

export default Tooltip
